/**
 * Document merger modal for combining clinical PDFs from the PA
 * three-dots menu.
 * @module documentMerger
 */
import type { PdfCompressResponse } from "@risalabs_frontend_org/oasis-ui-kit";
import { Modal, PdfMergePanel } from "@risalabs_frontend_org/oasis-ui-kit";
import { createElement, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { PdfCompressionService } from "../../../api/services/pdfCompressionService";
import { ModalId } from "../../../enums/modalId";
import { useResolvedStorageUrl } from "../../../hooks/useResolvedStorageUrl";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { formatDate } from "../../../utils/formatDate";
import { LoaderMessage } from "../../loaderMessage/loaderMessage";
import "./documentMerger.scss";
import { saveMergedDocument } from "./saveMergedDocument";
import { toPdfCompressionRequest } from "./toPdfCompressionRequest";
import { DocumentMergerMetaData } from "./types";

/** Self-hosted PDFium binary served from `public/`. */
const PDFIUM_WASM_URL = "/pdfium.wasm";

/**
 * Oasis modal hosting {@link PdfMergePanel} for merging order documents.
 *
 * Resolves the clinical-document Firebase Storage path to a signed download
 * URL before handing it to the merge panel. PDFium is loaded from
 * {@link PDFIUM_WASM_URL} with `worker: false` so wasm failures surface
 * instead of hanging on "Loading PDF…". When the user downloads the merged
 * packet, {@link saveMergedDocument} uploads it and upserts a
 * `submission_doc` row in Postgres using the panel's final
 * `download.documents` page mapping (not the pre-merge modal seed list).
 *
 * The merge panel starts with no pages selected (`defaultAllSelected={false}`)
 * and pre-fills the download title from order demographics.
 *
 * @returns Document merger modal with clinical PDF preview and merge UI
 */
const DocumentMerger = () => {
  const dispatch = useDispatch<AppDispatch>();
  const metaData = useSelector(
    (state: RootState) =>
      state.modal.metaData as DocumentMergerMetaData | undefined,
  );

  const {
    documents = [],
    src: storagePath = "",
    orderId,
    orgId,
    patientId,
    payerName,
    patientName,
    dateOfService,
  } = metaData ?? {};
  const {
    resolvedUrl: resolvedSrc,
    isLoading,
    loadError,
  } = useResolvedStorageUrl(storagePath);

  const onClose = () => {
    dispatch(closeModal());
  };

  const pathForFileUpload = useMemo(() => {
    return `medical-pa-orders/${orderId}/submission-document/${uuidv4()}.pdf`;
  }, [orderId]);

  /**
   * Default merged-PDF filename passed to {@link PdfMergePanel}.
   *
   * Pattern: `{patientName}_{payerName}_DOS-{YYYY-MM-DD}_ClinicalAttachments`.
   */
  const defaultMergedTitle = useMemo(() => {
    const formattedDateOfService = formatDate(dateOfService, "YYYY-MM-DD");
    return `${patientName}_${payerName}_DOS-${formattedDateOfService}_ClinicalAttachments`;
  }, [payerName, patientName, dateOfService]);

  return (
    <Modal
      dialogId={ModalId.DOCUMENT_MERGER_MODAL}
      onSave={() => {}}
      title={"Document Studio"}
      saveButtonText=""
      cancelText=""
      onClose={onClose}
      heightPercentage={90}
      hideFooter
    >
      {isLoading ? (
        <LoaderMessage message="Loading all documents..." />
      ) : (
        <>
          {loadError
            ? createElement(
                "p",
                { className: "px-4 py-2 text-sm text-red-600" },
                loadError,
              )
            : null}
          <PdfMergePanel
            src={resolvedSrc}
            documents={documents}
            height={"100%"}
            pdfRendererProps={{
              wasmUrl: PDFIUM_WASM_URL,
              worker: false,
            }}
            defaultAllSelected={false}
            defaultMergedTitle={defaultMergedTitle}
            onDownload={(download) => {
              saveMergedDocument({
                blob: download.blob,
                storagePath: pathForFileUpload,
                orderId: orderId ?? "",
                orgId: orgId ?? "",
                patientId: patientId ?? "",
                documents: download.renderedDocuments,
              });
            }}
            onCompress={async (compressionRequest) => {
              const response = await PdfCompressionService.compress(
                toPdfCompressionRequest(compressionRequest),
              );
              // The panel requires attempt_used even when a lossless result has none.
              return response as PdfCompressResponse;
            }}
          />
        </>
      )}
    </Modal>
  );
};

export default DocumentMerger;
